import { HttpApiService } from "@/shared/services/httpApi.service";
import type { PaginationParams } from "@/shared/types/pagination.types";

import { NOTIFICATIONS_API_ENDPOINTS, NOTIFICATIONS_PAGE_SIZE } from "../constants/notifications.constants";
import type { NotificationItem, NotificationsListResponse } from "../types/notifications-api.types";

export type FetchNotificationsParams = PaginationParams & {
  /** Optional override of the REST endpoint to call. The registry passes this in. */
  endpoint?: string;
};

export type MarkAsReadParams = {
  id: string;
  /** Optional override of the REST endpoint. The registry passes this in. */
  endpoint?: string;
};

/** What the server holds as unread: the first ids it lists (newest first) and how many there are in all. */
export interface UnreadNotifications {
  ids: string[];
  total: number;
}

/** The most the list endpoint returns in one page (`PaginationDto` max). */
const UNREAD_IDS_LIMIT = 200;

export const notificationsService = {
  fetchList: async (params?: FetchNotificationsParams): Promise<NotificationsListResponse> => {
    const path = params?.endpoint ?? NOTIFICATIONS_API_ENDPOINTS.NOTIFICATIONS;
    const queryParams: string[] = [];
    if (params?.skip != null) queryParams.push(`skip=${params.skip}`);
    if (params?.limit != null) queryParams.push(`limit=${params.limit}`);
    const url = queryParams.length ? `${path}?${queryParams.join("&")}` : path;
    const response = await HttpApiService.get<NotificationsListResponse>(url);
    return response.data;
  },

  /**
   * The notifications nobody has read yet, across every page (`unread_only=true`): their count, so a pending
   * one older than the loaded page still counts, and their ids, so reads and pushes that cross this request
   * can be told apart from what it already counted.
   */
  fetchUnread: async (endpoint?: string): Promise<UnreadNotifications> => {
    const path = endpoint ?? NOTIFICATIONS_API_ENDPOINTS.NOTIFICATIONS;
    const response = await HttpApiService.get<NotificationsListResponse>(
      `${path}?unread_only=true&skip=0&limit=${UNREAD_IDS_LIMIT}`,
    );
    return { ids: response.data.data.map((n) => n.id), total: response.data.total };
  },

  markAsRead: async (params: MarkAsReadParams): Promise<NotificationItem | void> => {
    const path = params.endpoint ?? NOTIFICATIONS_API_ENDPOINTS.markAsRead(params.id);
    const response = await HttpApiService.patch<NotificationItem>(path, {});
    return response.data;
  },
};

export const DEFAULT_NOTIFICATIONS_LIMIT = NOTIFICATIONS_PAGE_SIZE;
